import { useRef, useState } from "react";
import styled from "styled-components";
import api from "../../../Component/axios_Interceptor/api";
import { SERVER_ADDRESS } from "../../../Component/server";
import ChatModerationDialog from '../LiveList/Component/ChatModerationDialog';
import { publishChatMute } from '../../Chat/useChatMuteMask';

export default function ChatMuteControls({ onChanged, nickname: selectedNickname, confirmInDialog = false, muted = false, disabled = false, onMutedChange }: {
  onChanged?: () => void; nickname?: string; confirmInDialog?: boolean; muted?: boolean; disabled?: boolean; onMutedChange?: (muted: boolean) => void;
}) {
  const [muteNickname, setMuteNickname] = useState("");
  const [muteMessage, setMuteMessage] = useState("");
  const [muteError, setMuteError] = useState("");
  const [muteSaving, setMuteSaving] = useState(false);
  const mutePending = useRef(false);
  const [muteConfirmation,setMuteConfirmation] = useState<{ nickname: string; enabled: boolean } | null>(null);

  const applyChatMute = async () => {
    const nickname = muteConfirmation?.nickname ?? (selectedNickname ?? muteNickname).trim();
    const enabled = muteConfirmation?.enabled ?? !muted;
    if (!nickname || mutePending.current || disabled) return;
    mutePending.current = true;
    setMuteSaving(true); setMuteError(""); setMuteMessage("");
    try {
      const { data } = await api.post(`${SERVER_ADDRESS}realtime/chat-mute`, { nickname, enabled }, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (data?.muted !== enabled || data?.nickname !== nickname) throw new Error("설정 결과를 확인하지 못했습니다.");
      publishChatMute(nickname, enabled);
      onMutedChange?.(enabled);
      onChanged?.();
      setMuteMessage(enabled ? `${nickname} 님에게 채팅 금지를 적용했습니다.` : `${nickname} 님의 채팅 금지를 해제했습니다.`);
      setMuteConfirmation(null);
    } catch (failure: any) {
      setMuteError(failure?.response?.data?.message || "채팅 금지 설정을 확인하지 못했습니다. 같은 설정으로 다시 시도해주세요.");
    } finally {
      mutePending.current = false;
      setMuteSaving(false);
    }
  };

  return (
    <MuteControls aria-label="채팅 금지 관리">
      <MuteRow>
        {selectedNickname === undefined && <input aria-label="채팅 금지 적용할 사용자 닉네임" maxLength={100} value={muteNickname} disabled={muteSaving}
          onChange={event => { setMuteNickname(event.target.value); setMuteMessage(""); setMuteError(""); }}
          placeholder="채팅 금지 적용할 닉네임을 입력해주세요." />}
        <button type="button" data-chat-action="mute" disabled={muteSaving || disabled} onClick={() => {
          if (!confirmInDialog) { void applyChatMute(); return; }
          const nickname=(selectedNickname ?? muteNickname).trim();
          if (!nickname || mutePending.current) return;
          setMuteError('');setMuteConfirmation({ nickname, enabled: !muted });
        }}>{muted ? '채팅 금지 해제' : '채팅 금지'}</button>
      </MuteRow>
      {muteSaving && <p role="status">설정 중…</p>}
      {selectedNickname === undefined && muteMessage && <p role="status">{muteMessage}</p>}
      {muteError && !muteConfirmation && <p role="alert">{muteError}</p>}
      {muteConfirmation && <ChatModerationDialog action={muteConfirmation.enabled ? 'mute' : 'unmute'} nickname={muteConfirmation.nickname} saving={muteSaving} error={muteError}
        onConfirm={() => void applyChatMute()} onCancel={() => {if(!mutePending.current)setMuteConfirmation(null);}} />}
    </MuteControls>
  );
}

const MuteControls = styled.section`
  display: flex; flex-direction: column; gap: var(--space-2); min-width: 0;
  > p { margin: 0; color: var(--color-text-secondary); font-size: var(--text-caption); line-height: var(--leading-caption); }
  > [role='status'] { color: var(--admin-theme-success); }
  > [role='alert'] { color: var(--color-text-danger); }
`;

const MuteRow = styled.div`
  display: flex; gap: 8px;
  input, button { box-sizing: border-box; min-height: 42px; font: inherit; }
  input { min-width: 0; flex: 1; padding: 0 12px; border: 1px solid var(--admin-theme-border, #f0f0f0); border-radius: 8px;
    background: var(--color-bg-surface); color: var(--color-text-primary); }
  button { min-width: 54px; padding: 0 12px; border: 0; border-radius: 7px; color: #fff;
    background: var(--admin-solid, #292433); cursor: pointer; }
  button:hover:not(:disabled) { background: var(--color-bg-inverse-hover); }
  input:disabled, button:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); cursor: not-allowed; }
  input:focus-visible, button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;
